import { useQuery } from '@tanstack/react-query';
import {
  useCallback,
  useMemo,
  useRef,
  useState,
  type ChangeEvent,
  type Dispatch,
  type KeyboardEvent,
  type MutableRefObject,
  type SetStateAction,
  type SyntheticEvent,
} from 'react';

import { useSelectedWorkspaceSkillsQuery } from '@/features/skills/useSelectedWorkspaceSkills.js';
import { type Skill } from '@/lib/api/skills.js';
import { notify } from '@/lib/notify.js';
import { getRuntimeConfig } from '@/lib/runtime-config.js';

import { type SlashMenuSection } from '../../ui/chat/composer/slash-skill-match.js';
import {
  useSlashSkillPicker,
  type SelectedSkill,
  type SlashState,
} from '../../ui/chat/composer/useSlashSkillPicker.js';

/** Mirrors the chat composer's cap (`MAX_SKILLS` in `useComposer.ts`) — every surface that attaches
 *  skills via `/` should feel like the same limit, not an independently-chosen number. */
const MAX_SKILLS = 5;

/** The automation dialog's original wording, kept as the default so a caller that omits `copy`
 *  behaves exactly as `useAutomationSkillsField` always has. */
const AUTOMATION_COPY: UseSlashSkillsFieldCopy = {
  capNoun: 'automation',
  readyHint:
    'Type / to attach a skill. Attached skills are applied on every run.',
};

/** The cap warning and the ready helper text, so a caller other than an automation dialog (e.g. a
 *  quick action card) can speak in its own terms instead of inheriting "automation" wording. A caller
 *  that leaves `readyHint` out gets no helper text in the ready state — the chat already teaches `/`. */
export interface UseSlashSkillsFieldCopy {
  capNoun: string;
  readyHint?: string;
}

export interface UseSlashSkillsFieldArgs {
  /** The host's prompt textarea value. The hook only reads it; the caller owns the state. */
  prompt: string;
  /** Applies a text edit the hook computes — currently only the token-removal on a slash pick. */
  onPromptChange: (value: string) => void;
  /** The ids the host sends as `skill_ids` — the hook only reads and appends/removes. */
  skillIds: string[];
  onSkillIdsChange: (ids: string[]) => void;
  /** Overrides the cap-noun and ready-hint wording; defaults to the automation dialog's copy. */
  copy?: UseSlashSkillsFieldCopy;
  /** The workspace whose skills are offered. Left out, the field follows the switcher's selection. */
  workspaceId?: string | null;
  /** True while the host is still resolving `workspaceId`: nothing is fetched or offered until it
   *  settles, so the switcher's workspace never stands in for the host's own. */
  workspacePending?: boolean;
}

export interface UseSlashSkillsFieldResult {
  /** Attach to the Prompt `Textarea` — `SlashCommandMenu` anchors the popup to this element and a
   *  slash pick restores focus and caret through it. */
  textareaRef: MutableRefObject<HTMLTextAreaElement | null>;
  /**
   * Applies an edit the host makes itself — a button that types `/` at the caret — as the hook's own,
   * so the caret lands where the host puts it instead of being reset as an outside reseed, and the
   * popup opens on the token just written.
   */
  replaceText: (value: string, caret: number) => void;
  /** Wire to the Prompt `Textarea`'s `onChange`. */
  onChange: (e: ChangeEvent<HTMLTextAreaElement>) => void;
  /** Wire to the Prompt `Textarea`'s `onSelect` — re-syncs the caret on a click/arrow move that
   *  doesn't fire `onChange`. */
  onSelect: (e: SyntheticEvent<HTMLTextAreaElement>) => void;
  /** Wire to the Prompt `Textarea`'s `onKeyDown` — routes ArrowUp/ArrowDown/Enter/Escape to the
   *  popup while it is open; every other key is left alone. */
  onKeyDown: (e: KeyboardEvent<HTMLTextAreaElement>) => void;
  /** The `features.skills` kill switch — false means render the bare textarea with no popup, no
   *  chips, and no helper text at all. */
  skillsEnabled: boolean;
  slashState: SlashState | null;
  slashSections: SlashMenuSection<Skill>[];
  slashHighlightIndex: number;
  setSlashHighlightIndex: Dispatch<SetStateAction<number>>;
  selectSlashSkill: (skill: Skill) => void;
  dismissSlash: () => void;
  /** The library the `/` popup lists — the same scoped fetch the chips resolve against, empty until
   *  it lands. */
  library: Skill[];
  /** The picked skills, resolved to title/trigger against the fetched library — drives `SkillChips`. */
  selectedSkills: SelectedSkill[];
  removeSkill: (id: string) => void;
  /** Below-the-field copy — distinguishes "still loading" from "the library is genuinely empty" so
   *  neither state is ever told to the user while the other is still true. `null` while the kill
   *  switch is off. */
  helperText: string | null;
}

/**
 * Wires the chat composer's slash-command skill picker (`useSlashSkillPicker`) onto any host's
 * Prompt field. This hook owns none of the token/popup mechanics itself — that stays in
 * `useSlashSkillPicker`, exactly as the chat composer uses it — it only bridges a plain controlled
 * textarea (caret tracking, the keyboard routing `Composer.tsx` wires by hand, and resolving the
 * host's `skillIds` list to full `SelectedSkill`s for the chips) so every surface that attaches
 * skills via `/` — the automation dialogs and the quick-actions Customize sheet — can share the
 * exact same wiring. `copy` lets a non-automation host speak in its own terms (see
 * `UseSlashSkillsFieldCopy`); `useAutomationSkillsField` is this hook under its original name,
 * kept as a re-export so the automation dialogs and their tests need no change.
 */
export function useSlashSkillsField({
  prompt,
  onPromptChange,
  skillIds,
  onSkillIdsChange,
  copy = AUTOMATION_COPY,
  workspaceId,
  workspacePending = false,
}: UseSlashSkillsFieldArgs): UseSlashSkillsFieldResult {
  const skillsEnabled = getRuntimeConfig().features.skills;
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);
  /**
   * Starts at **0**, not `prompt.length` — a mount is exactly the same hazard the reseed
   * comment below describes: a saved prompt ending in a real `/word` (e.g. "summarize the
   * sprint /status") must not pop the popup open before the user has touched anything, and a
   * fresh mount has no `lastKnownPromptRef` history to compare against, so it can never catch
   * this the way the reseed branch does — the initial value has to already be safe. This
   * matters beyond the automation dialogs (which stay mounted, so this only ever runs once on
   * first render): the quick-actions Customize sheet keys its editor by slot index, so every
   * slot switch — and every first open of a slot with such a prompt — fully remounts this hook.
   */
  const [caretPos, setCaretPos] = useState(0);

  /**
   * The create dialog stays mounted and re-seeds `prompt` from outside this hook — `reset()` on
   * close — bypassing `onChange`/`onTokenReplaced`
   * entirely. Without this, `caretPos` would carry whatever it last was for a *different* prompt,
   * which can accidentally land inside (or past the end of) the freshly seeded text. Comparing
   * against the last value this hook itself produced — updated alongside `caretPos` in `onChange`
   * and `onTokenReplaced` below — is what tells an external reseed apart from the hook's own edit.
   *
   * The reseed goes to **0**, not the end of the new text — the same reason the initial value
   * above is 0: a stored prompt ending in a real `/word` must not pop the popup open over the
   * dialog before the user has touched anything — `SlashCommandMenu` renders purely off
   * `slashState.open`, with no regard for whether the field is even focused. Caret 0 has no token
   * before it, so no popup opens on reseed; the first real `onSelect`/`onChange` (an actual click
   * or keystroke) re-syncs the caret to wherever the user really put it, and only then can typing
   * or clicking at a trailing token open the popup — the same as it would for freshly typed text.
   */
  const lastKnownPromptRef = useRef(prompt);
  if (prompt !== lastKnownPromptRef.current) {
    lastKnownPromptRef.current = prompt;
    setCaretPos(0);
  }

  /**
   * Gated, not merely unrendered: the kill switch should leave no request behind either. While the
   * workspace is pending, a library cached under the placeholder key is ignored as well.
   */
  const skillsQuery = useQuery({
    ...useSelectedWorkspaceSkillsQuery(workspaceId),
    enabled: skillsEnabled && !workspacePending,
  });
  const skills = workspacePending ? undefined : skillsQuery.data;
  const skillsLoading = workspacePending || skillsQuery.isPending;
  const skillsFailed = !workspacePending && skillsQuery.isError;

  const selectedSkills = useMemo<SelectedSkill[]>(() => {
    if (skills) {
      /**
       * One chip per attached id, including an id the catalog no longer has — a skill deleted
       * since the host was saved. Dropping it instead would strand the user: the id stays in
       * `skillIds` and gets sent on save, where the authoring resolver rejects it with a 422, yet
       * there is no chip to remove and nothing to explain the refusal. It would also make the cap
       * count the visible chips rather than the attached ones, letting a 5-skill host take on more.
       * Same wording as the detail page, so a deleted skill reads the same in both places.
       */
      return skillIds.map((id) => {
        const found = skills.find((skill) => skill.id === id);
        return found
          ? { id: found.id, title: found.title, trigger: found.trigger }
          : { id, title: 'Unavailable skill', trigger: id };
      });
    }
    if (skillsFailed) {
      /**
       * A fetch error, not a first load: showing zero chips would tell the user their own host has
       * no skills attached, which is false when `skillIds` is non-empty. Fall back to id-only
       * placeholders — a title-less chip is honest about what's known; silence is not.
       */
      return skillIds.map((id) => ({ id, title: id, trigger: id }));
    }
    /**
     * Still loading, never fetched before: the raw id is not the user's data in the way a fetch
     * error's stale-but-real ids are, it is only an implementation detail (a uuid) that happens to
     * be sitting in `skillIds` before the title to go with it has arrived. Rendering it as a chip
     * would flash a uuid where a skill name belongs on every open — show nothing until the real
     * title resolves; `helperText`'s "Loading your skills…" already covers this window.
     */
    return [];
  }, [skillIds, skills, skillsFailed]);

  const addSkill = useCallback(
    (skill: SelectedSkill): void => {
      if (skillIds.includes(skill.id)) return;
      onSkillIdsChange([...skillIds, skill.id]);
    },
    [skillIds, onSkillIdsChange],
  );

  const removeSkill = useCallback(
    (id: string): void => {
      onSkillIdsChange(skillIds.filter((skillId) => skillId !== id));
    },
    [skillIds, onSkillIdsChange],
  );

  /** Applies the draft edit a slash pick implies, then restores focus and caret — mirrors
   *  `useComposer`'s `applySlashTokenRemoval`. */
  const onTokenReplaced = useCallback(
    (nextValue: string, nextCaretPos: number): void => {
      onPromptChange(nextValue);
      lastKnownPromptRef.current = nextValue;
      setCaretPos(nextCaretPos);
      requestAnimationFrame(() => {
        const el = textareaRef.current;
        if (!el) return;
        el.focus();
        el.setSelectionRange(nextCaretPos, nextCaretPos);
      });
    },
    [onPromptChange],
  );

  /** At the chip cap, unlike the chat composer, this field has no in-progress message to protect —
   *  a stuck `/trigger` left in a saved prompt would ship to the agent (or run) verbatim.
   *  `onCapExceeded` opts into stripping the token anyway; this is the caller's chance to say why. */
  const onCapExceeded = useCallback((): void => {
    notify.warning(
      `Only ${MAX_SKILLS} skills per ${copy.capNoun} — remove one before attaching another.`,
    );
  }, [copy.capNoun]);

  const {
    slashState,
    slashSections,
    slashMatches,
    slashHighlightIndex,
    setSlashHighlightIndex,
    selectSlashSkill,
    dismissSlash,
  } = useSlashSkillPicker({
    value: prompt,
    caretPos,
    skillsEnabled,
    skills,
    selectedSkills,
    maxSkills: MAX_SKILLS,
    addSkill,
    onTokenReplaced,
    onCapExceeded,
  });

  const onChange = useCallback(
    (e: ChangeEvent<HTMLTextAreaElement>): void => {
      const nextValue = e.target.value;
      onPromptChange(nextValue);
      lastKnownPromptRef.current = nextValue;
      setCaretPos(e.target.selectionStart ?? nextValue.length);
    },
    [onPromptChange],
  );

  const onSelect = useCallback(
    (e: SyntheticEvent<HTMLTextAreaElement>): void => {
      setCaretPos(e.currentTarget.selectionStart ?? 0);
    },
    [],
  );

  /** Routes ArrowUp/ArrowDown/Enter/Tab/Escape to the popup while it's open — the same keys
   *  `useComposer.onKeyDown` handles for the slash popup, since keyboard focus never leaves the
   *  textarea and the popup has no focus of its own (see `SlashCommandMenu`'s JSDoc). */
  const onKeyDown = useCallback(
    (e: KeyboardEvent<HTMLTextAreaElement>): void => {
      const slashOpen = slashState !== null && slashState.open;

      if (
        slashOpen &&
        (e.key === 'ArrowDown' || e.key === 'ArrowUp') &&
        slashMatches.length > 0
      ) {
        e.preventDefault();
        const delta = e.key === 'ArrowDown' ? 1 : -1;
        setSlashHighlightIndex(
          (prev) => (prev + delta + slashMatches.length) % slashMatches.length,
        );
        return;
      }

      if (slashOpen && e.key === 'Escape') {
        e.preventDefault();
        dismissSlash();
        return;
      }

      if (
        slashOpen &&
        (e.key === 'Enter' || e.key === 'Tab') &&
        !e.shiftKey &&
        slashMatches.length > 0
      ) {
        e.preventDefault();
        const index = Math.min(slashHighlightIndex, slashMatches.length - 1);
        selectSlashSkill(slashMatches[index]!);
      }
    },
    [
      slashState,
      slashMatches,
      slashHighlightIndex,
      setSlashHighlightIndex,
      dismissSlash,
      selectSlashSkill,
    ],
  );

  /**
   * Loading, a fetch error, and "the library is genuinely empty" must never be told to the user
   * interchangeably. Cached data is checked BEFORE the error flag: a background refetch (opening
   * the popup revalidates on every open — see `useSlashSkillPicker`) can fail while a perfectly
   * good list from the last successful fetch is still sitting in `skills`, and that list — not an
   * error banner — is what the user should see over data they can already see rendered as chips.
   * Only when there has never been a successful fetch does the error copy show, and only once the
   * query has actually settled does "empty" get said at all (an in-flight query must never read as
   * empty, and a settled error must never read as an empty library). Typing `/` is itself a retry —
   * `useSlashSkillPicker` revalidates the cached list the moment a slash token appears — so the
   * error copy points at that instead of a separate retry control this field doesn't have room for.
   */
  const helperText = !skillsEnabled
    ? null
    : skillsLoading
      ? 'Loading your skills…'
      : skills
        ? skills.length === 0
          ? 'No skills yet — create one from Skills, then type / here to attach it.'
          : (copy.readyHint ?? null)
        : "Couldn't load your skills — type / to retry.";

  return {
    textareaRef,
    replaceText: onTokenReplaced,
    onChange,
    onSelect,
    onKeyDown,
    skillsEnabled,
    slashState,
    slashSections,
    slashHighlightIndex,
    setSlashHighlightIndex,
    selectSlashSkill,
    dismissSlash,
    library: skills ?? [],
    selectedSkills,
    removeSkill,
    helperText,
  };
}
